<template>
  <div
    class="SearchGallery">

    <FilterBar
      class="SearchGallery_FilterBar"
      :title="title"
      :filters="computedFilters"
      :is-loading="isLoading"
      :value="activeFilters"
      @filter-input="handleFilterInput"
      @clear-group="handleClearFilterGroup"
    />

    <FilterMenu
      :showing="showMobileFilterMenu"
      :filters="computedFilters"
      :value="activeFilters"
      @filter-input="handleFilterInput"
      @close="showMobileFilterMenu = false"
    />

    <div
      class="SearchGallery_Gallery">
      <SearchGalleryHeader
        class="SearchGallery_SearchGalleryHeader"
        :search-text="searchText"
        :is-loading="isLoading"
        :is-searching="searchingApps"
        :active-filters="activeFilters"
        :results-text="computedResultsText"
        :sort-options="sortOptions"
        :sort-value="sortValue"
        @search="handleSearch"
        @filter-input="handleFilterInput"
        @filter-menu-btn-click="showMobileFilterMenu = true"
        @sort="handleSort"
      />

      <SearchGalleryResults
        class="SearchGallery_SearchGalleryResults"
        :results="filteredApps"
        :is-loading="isLoading"
        :is-logos-square="isLogosSquare"
        @click-result="handleResultClick"
      />
    </div>

  </div>
</template>

<script>
import { debounce, orderBy } from 'lodash';
import ArrayUtilsMixin from '@/mixins/array-utils';
import StringUtilsMixin from '@/mixins/string-utils';
import FilterBar from '@/components/FilterBar.vue';
import FilterMenu from '@/components/FilterMenu.vue';
import SearchGalleryHeader from '@/components/SearchGalleryHeader.vue';
import SearchGalleryResults from '@/components/SearchGalleryResults.vue';

export default {
  name: 'SearchGallery',

  mixins: [
    ArrayUtilsMixin,
    StringUtilsMixin,
  ],

  components: {
    FilterBar,
    FilterMenu,
    SearchGalleryHeader,
    SearchGalleryResults,
  },

  data() {
    return {
      searchText: this.$route.query.q || '',
      filterText: this.$route.query.q || '',
      searchingApps: false,

      showMobileFilterMenu: false,

      activeFilters: this.activeFiltersFromQueryParams(this.$route.query),
    };
  },

  props: {
    title:         { type: String,  default: null },
    apps:          { type: Array,   default: () => [] },
    isLoading:     { type: Boolean, default: true },
    filterMap:     { type: Array,   required: true },
    filters:       { type: Object,  default: null },
    searchFields:  { type: Array,   default: () => [] },
    sortOptions:   { type: Array,   default: null },
    sortValue:     { type: Object,  default: null },
    isLogosSquare: { type: Boolean, default: true },
  },

  computed: {
    computedFilters() {
      return this.filterMap.map((mappedFilter) => ({
        ...mappedFilter,
        filters: this.filters
          ? this.filters[mappedFilter.key].map((f) => ({ label: f, value: f }))
          : [],
      }));
    },

    computedResultsText() {
      if (this.isLoading) return 'Loading Results';
      if (this.filterText || this.activeFilters.length) {
        return `Displaying ${this.filteredApps.length} Results`;
      }
      return 'Displaying All Results';
    },

    groupedActiveFilters() {
      const grouped = {};

      this.activeFilters.forEach((activeFilter) => {
        if (grouped[activeFilter.groupKey] === undefined) {
          grouped[activeFilter.groupKey] = [];
        }
        grouped[activeFilter.groupKey].push(activeFilter.value);
      });

      return grouped;
    },

    filteredApps() {
      if (this.isLoading) {
        return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20]
          .map((i) => ({ appstore_id: `placeholder${i}` }));
      }

      let filteredApps = this.apps;

      // 1. Filter by search text
      filteredApps = filteredApps.filter((app) => {
        const searchText = this.filterText.toLowerCase();

        return this.searchFields.some((field) => (
          app[field]
            ? this.iterableContainsString(app[field], searchText)
            : false
        ));
      });

      // 2. Filter by filters (inside group = OR, outside group = AND)
      Object.keys(this.groupedActiveFilters).forEach((filterKey) => {
        filteredApps = filteredApps.filter((app) => {
          if (!app[filterKey]) return false;

          const filterMethod = (typeof app[filterKey] === 'string')
            ? this.iterableContainsString
            : this.arrayContainsAnyFromArray;

          return filterMethod(this.groupedActiveFilters[filterKey], app[filterKey]);
        });
      });

      // 3. Sort & return results
      if (
        !this.sortValue
        || !filteredApps.length
        || !filteredApps[0][this.sortValue.field]
      ) return filteredApps;

      return orderBy(
        filteredApps,
        [(app) => this.lowerCaseNoSpace(app[this.sortValue.field])],
        [this.sortValue.order],
      );
    },
  },

  watch: {
    searchText(val) {
      this.searchingApps = true;
      this.searchApps(val);
    },
  },

  async created() {
    this.searchApps = debounce(this.fetchApps, 250);
  },

  methods: {
    fetchApps() {
      /* @NOTE
       * If we switch to remote filtering/pagination, use
       * this function asynchronously and fetch data here..
       */
      this.filterText = this.searchText;

      this.searchingApps = false;

      this.$router.replace({
        name: this.$route.name,
        query: this.filterText
          ? { q: this.filterText, ...this.activeFiltersToQueryParams(this.activeFilters) }
          : { ...this.activeFiltersToQueryParams(this.activeFilters) },
      });
    },

    handleResultClick(app) {
      this.$emit('click-result', app);
    },

    handleSearch(text) {
      this.searchText = text.trim();
    },

    handleSort(sortValue) {
      this.$emit('sort', sortValue);
    },

    handleFilterInput(groupKey, filter) {
      this.$analytics.trackLink({
        position: `app-filter: ${groupKey}`,
        name: `${filter.label} ${([...this.activeFilters].filter((f) => f.value === filter.value).length > 0) ? 'unchecked' : 'checked'}`,
        path: this.$route.path,
      });

      let newValue = [...this.activeFilters].filter((f) => (
        `${f.groupKey}${f.value}` !== `${groupKey}${filter.value}`
      ));

      if (this.activeFilters.length === newValue.length) {
        newValue = [
          ...this.activeFilters,
          { groupKey, label: filter.label, value: filter.value },
        ];
      }

      this.activeFilters = newValue;

      const filterQueryParams = this.activeFiltersToQueryParams(newValue);

      this.$router.replace({
        name: this.$route.name,
        query: this.filterText
          ? { q: this.filterText, ...filterQueryParams }
          : { ...filterQueryParams },
      });
    },

    handleClearFilterGroup(groupKey) {
      this.activeFilters = this.activeFilters.filter((f) => (f.groupKey !== groupKey));

      const filterQueryParams = this.activeFiltersToQueryParams(this.activeFilters);

      this.$router.replace({
        name: this.$route.name,
        query: this.filterText
          ? { q: this.filterText, ...filterQueryParams }
          : { ...filterQueryParams },
      });
    },

    activeFiltersToQueryParams(filters) {
      const rawParams = {};

      this.filterMap.forEach((mapItem) => {
        rawParams[mapItem.key] = filters
          .filter((f) => f.groupKey === mapItem.key)
          .map((f) => f.value)
          .sort()
          .join(',');
      });

      const cleanedParams = {};

      Object.keys(rawParams).forEach((p) => {
        if (rawParams[p].length) {
          cleanedParams[p] = rawParams[p];
        }
      });

      return cleanedParams;
    },

    activeFiltersFromQueryParams(queryParams) {
      const allowedParamsNames = this.filterMap.map((f) => f.key);

      const activeFilters = [];

      if (!queryParams) return activeFilters;

      Object.keys(queryParams).forEach((paramName) => {
        if (!allowedParamsNames.includes(paramName)) return;

        const activeGroupFilters = queryParams[paramName]
          .split(',')
          .map((filterValue) => ({
            groupKey: paramName,
            label: filterValue,
            value: filterValue,
          }));

        activeFilters.push(...activeGroupFilters);
      });

      return activeFilters;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/_variables';

$filter-bar-width: 250px;

.SearchGallery {
  position: relative;
  height: 100%;
}

.SearchGallery_Gallery {
  display: flex;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: $filter-bar-width;
  flex-direction: column;
}

.SearchGallery_SearchGalleryHeader {
  padding: 24px 32px 16px 32px;
}

.SearchGallery_SearchGalleryResults {
  padding: 0 20px 48px 20px;
}

@media only screen and (max-width: 640px) {
  .SearchGallery_FilterBar {
    display: none;
  }

  .SearchGallery_Gallery {
    left: 0 !important;
  }

  .SearchGallery_SearchGalleryHeader {
    padding: 16px 16px 8px 16px !important;
  }

  .SearchGallery_SearchGalleryResults {
    padding: 0 4px 32px 4px !important;
  }
}
</style>
